function hasFrequencyEntries(candidate) {
  return (
    Array.isArray(candidate?.freqs) ||
    Array.isArray(candidate?.frequencies)
  );
}

/**
 * Resolves microphone calibration from both current and older DTO shapes.
 *
 * Averaged calibration components have changed shape between BE iterations.
 * Depending on the endpoint/version, microphoneCalibration can be:
 * - the value passed directly,
 * - a sibling of deviceCalibration on the component,
 * - nested in deviceCalibration,
 * - nested in averageDeviceCalibration/calibration.
 *
 * Keeping this compatibility in one helper prevents the averaged views from
 * silently losing microphone values when only the DTO wrapper changes.
 */
export function resolveMicrophoneCalibration(...sources) {
  for (const source of sources) {
    const candidates = [
      source,
      source?.microphoneCalibration,
      source?.deviceCalibration?.microphoneCalibration,
      source?.averageDeviceCalibration?.microphoneCalibration,
      source?.calibration?.microphoneCalibration,
    ];

    const resolved = candidates.find(hasFrequencyEntries);
    if (resolved) {
      return resolved;
    }
  }

  return null;
}

export function hasMicrophoneCalibration(...sources) {
  return resolveMicrophoneCalibration(...sources) !== null;
}

export function getMicrophoneFrequencyCalibration(
  microphoneCalibrationOrContainer,
  frequency,
) {
  const microphoneCalibration = resolveMicrophoneCalibration(
    microphoneCalibrationOrContainer,
  );

  const frequencyEntries = Array.isArray(microphoneCalibration?.freqs)
    ? microphoneCalibration.freqs
    : Array.isArray(microphoneCalibration?.frequencies)
      ? microphoneCalibration.frequencies
      : [];

  return (
    frequencyEntries.find(
      (item) =>
        Number(item?.freq ?? item?.frequency) === Number(frequency),
    ) || null
  );
}

export function getMicrophoneSignalPower(
  microphoneCalibrationOrContainer,
  frequency,
) {
  const frequencyCalibration = getMicrophoneFrequencyCalibration(
    microphoneCalibrationOrContainer,
    frequency,
  );

  return (
    frequencyCalibration?.measuredSignalPower ??
    frequencyCalibration?.measuredSignalPowerDb ??
    null
  );
}

export function formatMicrophoneSignalPower(value) {
  if (value == null || typeof value !== "number" || Number.isNaN(value)) {
    return "Brak Danych";
  }

  const sign = value < 0 ? -1 : 1;
  return (
    (sign * Math.round((Math.abs(value) + Number.EPSILON) * 10)) / 10
  );
}
